import React from "react";
import Icon from "./icons";

// V2 quantity stepper: two bm-border circle buttons around a bm-label-l
// count. The value is clamped to [min, max] and the button that would cross
// a bound is marked aria-disabled and does nothing. It stays focusable on
// purpose: a native `disabled` would drop keyboard focus to the page body as
// soon as the count reached a bound, and BmModal's focus trap would then send
// the next Tab back to the dialog's first control instead of the next field.
// `label` names the group and both buttons for readers ("Increase Story"), so
// a list of steppers never repeats the same name.
// `variant="boxed"` is the phone counter form's stepper (Figma 2023:1583):
// one bordered box with three cells (minus, count, plus). Opt-in only; the
// default draws the circles exactly as before.

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

// The icon set has no minus, so draw one on the same 24px grid and stroke.
const MinusGlyph = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    className="shrink-0"
    aria-hidden="true"
    focusable="false"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const BUTTON =
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-bm-border bg-white text-bm-ink transition-colors hover:bg-bm-subtle aria-disabled:cursor-not-allowed aria-disabled:text-bm-tertiary aria-disabled:hover:bg-white";

// Boxed cells: px-3 py-2 around a bm-label-l line; the glyphs are muted.
const BOXED_BUTTON =
  "flex h-[34px] shrink-0 items-center justify-center px-3 text-bm-muted transition-colors hover:bg-bm-subtle aria-disabled:cursor-not-allowed aria-disabled:text-bm-tertiary aria-disabled:hover:bg-transparent";

const QtyStepper = ({ value, onChange, min = 0, max = 99, label, className = "", variant = "circle" }) => {
  const current = clamp(Number(value) || 0, min, max);
  const atMin = current <= min;
  const atMax = current >= max;
  const name = (verb) => (label ? `${verb} ${label}` : verb);
  const boxed = variant === "boxed";
  const buttonClass = boxed ? BOXED_BUTTON : BUTTON;
  return (
    <div
      role="group"
      aria-label={label}
      className={
        boxed
          ? `flex shrink-0 items-stretch overflow-hidden rounded-[8px] border border-bm-border bg-white ${className}`.trim()
          : `flex items-center gap-3 ${className}`.trim()
      }
    >
      <button
        type="button"
        aria-label={name("Decrease")}
        aria-disabled={atMin || undefined}
        onClick={() => !atMin && onChange(clamp(current - 1, min, max))}
        className={buttonClass}
      >
        <MinusGlyph />
      </button>
      <span
        aria-live="polite"
        className={
          boxed
            ? "flex min-w-[32px] items-center justify-center px-3 text-center tabular-nums bm-label-l text-bm-ink"
            : "min-w-[24px] text-center tabular-nums bm-label-l text-bm-ink"
        }
      >
        {current}
      </span>
      <button
        type="button"
        aria-label={name("Increase")}
        aria-disabled={atMax || undefined}
        onClick={() => !atMax && onChange(clamp(current + 1, min, max))}
        className={buttonClass}
      >
        <Icon name="plus" size={14} />
      </button>
    </div>
  );
};

export default QtyStepper;
